'use client';

import { KeyRound } from 'lucide-react';

import { useSignInWithPasskey } from '@kit/supabase/hooks/use-sign-in-with-passkey';
import { setPasskeyClientHint } from '@kit/supabase/passkey-client-hint';
import { Button, type ButtonProps } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import { AuthErrorAlert } from './auth-error-alert';

export function PasskeySignInButton({
  className,
  onError,
  onSignIn,
  showError = true,
  variant = 'outline',
}: {
  className?: string;
  onError?: (error: Error | string | null) => unknown;
  onSignIn?: (userId?: string) => unknown;
  showError?: boolean;
  variant?: ButtonProps['variant'];
}) {
  const signInMutation = useSignInWithPasskey();

  const signIn = async () => {
    onError?.(null);

    try {
      const data = await signInMutation.mutateAsync();
      setPasskeyClientHint(true);
      onError?.(null);
      onSignIn?.(data.user?.id);
    } catch (error) {
      onError?.(getAuthError(error));
    }
  };

  return (
    <div className={'space-y-3'}>
      {showError ? <AuthErrorAlert error={signInMutation.error} /> : null}

      <Button
        aria-label={'Sign in with passkey'}
        className={cn('flex w-full gap-2 text-center', className)}
        data-test={'passkey-sign-in-button'}
        disabled={signInMutation.isPending}
        type={'button'}
        variant={variant}
        onClick={() => void signIn()}
      >
        <KeyRound className={'h-4 w-4'} />
        <span className={'truncate'}>Passkey</span>
      </Button>
    </div>
  );
}

function getAuthError(error: unknown) {
  if (error instanceof Error || typeof error === 'string') {
    return error;
  }

  return new Error('default');
}
